import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, IconButton, Chip } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { apiCall } from '../api/config';

const FILTERS = [
  { key: 'tutti', label: 'Tutti' },
  { key: 'donna', label: 'Donna' },
  { key: 'uomo', label: 'Uomo' },
];

// Sotto-categorie mostrate sotto il filtro Donna/Uomo.
// La key matcha il campo `categoria` del prodotto in Firebase.
const SUBCATEGORIES_BY_GENDER = {
  donna: [
    { key: 'vestito', label: 'Vestiti' },
    { key: 'gonna', label: 'Gonne' },
    { key: 'pantalone', label: 'Pantaloni' },
    { key: 'giacca', label: 'Giacche' },
    { key: 'giubbino', label: 'Giubbini' },
  ],
  uomo: [
    { key: 'blazer', label: 'Blazer' },
    { key: 'giubbino', label: 'Giubbini' },
    { key: 'pantalone', label: 'Pantaloni' },
    { key: 'jeans', label: 'Jeans' },
  ],
};

const CATALOG_CACHE_KEY = 'catalog_products_v2';
const CATALOG_CACHE_TTL_MS = 5 * 60 * 1000; // 5 min
const SKELETON_COUNT = 6;

// Shopify CDN serves thumbnails natively by injecting _WIDTHx before the
// extension. 400px is right-sized for a 2-col mobile grid at 2x DPR.
function getThumbUrl(url, width = 400) {
  if (!url || typeof url !== 'string') return url;
  if (!url.includes('cdn.shopify.com')) return url;
  return url.replace(/(\.(jpg|jpeg|png|webp|gif))(\?|$)/i, `_${width}x$1$3`);
}

function readCatalogCache() {
  try {
    const raw = sessionStorage.getItem(CATALOG_CACHE_KEY);
    if (!raw) return null;
    const { ts, data } = JSON.parse(raw);
    if (Date.now() - ts > CATALOG_CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function writeCatalogCache(data) {
  try {
    sessionStorage.setItem(CATALOG_CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    // quota exceeded or serialization error — ignore
  }
}

function CatalogBrowse() {
  const navigate = useNavigate();
  const cached = readCatalogCache();
  const [products, setProducts] = useState(cached || []);
  const [loading, setLoading] = useState(!cached);
  const [activeFilter, setActiveFilter] = useState('tutti');
  const [activeCategory, setActiveCategory] = useState(null);

  const handleSelectFilter = (key) => {
    setActiveFilter(key);
    setActiveCategory(null);
  };

  useEffect(() => {
    const loadProducts = async () => {
      try {
        // /api/catalog: server-trimmed payload + Vercel edge cache.
        // Falls through to the sessionStorage cache if the network is slow.
        const res = await apiCall('/catalog');
        const catalogItems = res?.items || [];
        setProducts(catalogItems);
        writeCatalogCache(catalogItems);
      } catch (err) {
        console.error('Errore caricamento catalogo:', err);
      } finally {
        setLoading(false);
      }
    };
    // Refresh in background even if we have cache (serves stale-while-revalidate)
    loadProducts();
  }, []);

  const filteredProducts = products.filter(p => {
    if (activeFilter !== 'tutti' && p.genere !== activeFilter) return false;
    if (activeCategory && p.categoria !== activeCategory) return false;
    return true;
  });

  const subcategories = SUBCATEGORIES_BY_GENDER[activeFilter] || null;

  const handleSelectItem = (item) => {
    localStorage.setItem('currentProduct', JSON.stringify({
      id: item.id,
      name: item.name || item.nome,
      price: item.price || item.prezzo,
      originalPrice: item.originalPrice,
      imageUrl: item.imageUrl || item.immagine_url,
      category: item.category || item.categoria,
      subcategory: item.subcategory || item.sottocategoria,
      brand: item.brand,
      genere: item.genere,
      colore_principale: item.colore_principale,
      url_originale: item.url_originale,
      source: 'catalog',
    }));
    navigate('/photo-capture');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #1a1a2e 0%, #16213e 40%, #0f3460 100%)',
        pt: 'env(safe-area-inset-top)',
        pb: 'env(safe-area-inset-bottom)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 2,
          pt: 'calc(12px + env(safe-area-inset-top))',
          pb: 1,
        }}
      >
        <IconButton
          onClick={() => navigate('/')}
          sx={{
            color: 'white',
            background: 'rgba(255,255,255,0.1)',
            backdropFilter: 'blur(10px)',
            '&:hover': { background: 'rgba(255,255,255,0.2)' },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography
            sx={{
              color: 'white',
              fontSize: { xs: '22px', sm: '26px' },
              fontWeight: 800,
              letterSpacing: '-0.5px',
              lineHeight: 1.1,
            }}
          >
            Catalogo
          </Typography>
          <Typography
            sx={{
              color: 'rgba(255,255,255,0.5)',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            {filteredProducts.length} {filteredProducts.length === 1 ? 'capo' : 'capi'} disponibili
          </Typography>
        </Box>
      </Box>

      {/* Filter chips — sticky at top while scrolling */}
      <Box
        sx={{
          position: 'sticky',
          top: 'env(safe-area-inset-top)',
          zIndex: 10,
          display: 'flex',
          gap: 0.75,
          px: 2,
          py: 0.75,
          overflowX: 'auto',
          background: 'rgba(22, 33, 62, 0.82)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {FILTERS.map(f => (
          <Chip
            key={f.key}
            label={f.label}
            onClick={() => handleSelectFilter(f.key)}
            sx={{
              px: 0.5,
              height: '28px',
              fontSize: '13px',
              fontWeight: 600,
              borderRadius: '14px',
              border: '1px solid',
              borderColor: activeFilter === f.key ? 'transparent' : 'rgba(255,255,255,0.18)',
              background: activeFilter === f.key
                ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                : 'rgba(255,255,255,0.06)',
              color: activeFilter === f.key ? 'white' : 'rgba(255,255,255,0.65)',
              transition: 'all 0.2s ease',
              '& .MuiChip-label': { px: 1.25 },
              '&:hover': {
                background: activeFilter === f.key
                  ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                  : 'rgba(255,255,255,0.12)',
              },
            }}
          />
        ))}
      </Box>

      {/* Sotto-categorie (visibili solo con filtro Donna o Uomo) */}
      {subcategories && (
        <Box
          sx={{
            position: 'sticky',
            top: 'calc(env(safe-area-inset-top) + 44px)',
            zIndex: 9,
            display: 'flex',
            gap: 0.75,
            px: 2,
            py: 0.75,
            overflowX: 'auto',
            background: 'rgba(22, 33, 62, 0.7)',
            backdropFilter: 'blur(18px)',
            WebkitBackdropFilter: 'blur(18px)',
            borderBottom: '1px solid rgba(255,255,255,0.06)',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Chip
            label="Tutte"
            onClick={() => setActiveCategory(null)}
            sx={{
              px: 0.5,
              height: '26px',
              fontSize: '12px',
              fontWeight: 600,
              borderRadius: '13px',
              border: '1px solid',
              borderColor: !activeCategory ? 'transparent' : 'rgba(255,255,255,0.15)',
              background: !activeCategory
                ? 'rgba(255,255,255,0.18)'
                : 'rgba(255,255,255,0.04)',
              color: !activeCategory ? 'white' : 'rgba(255,255,255,0.55)',
              '& .MuiChip-label': { px: 1.1 },
              '&:hover': {
                background: !activeCategory ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.1)',
              },
            }}
          />
          {subcategories.map(c => (
            <Chip
              key={c.key}
              label={c.label}
              onClick={() => setActiveCategory(c.key)}
              sx={{
                px: 0.5,
                height: '26px',
                fontSize: '12px',
                fontWeight: 600,
                borderRadius: '13px',
                border: '1px solid',
                borderColor: activeCategory === c.key ? 'transparent' : 'rgba(255,255,255,0.15)',
                background: activeCategory === c.key
                  ? 'rgba(255,255,255,0.18)'
                  : 'rgba(255,255,255,0.04)',
                color: activeCategory === c.key ? 'white' : 'rgba(255,255,255,0.55)',
                '& .MuiChip-label': { px: 1.1 },
                '&:hover': {
                  background: activeCategory === c.key ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.1)',
                },
              }}
            />
          ))}
        </Box>
      )}

      {/* Loading — skeleton grid matches real card layout so perceived speed is high */}
      {loading && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 2,
            px: 2,
            pt: 2,
            pb: 'calc(24px + env(safe-area-inset-bottom))',
          }}
        >
          {Array.from({ length: SKELETON_COUNT }).map((_, i) => (
            <Box
              key={i}
              sx={{
                borderRadius: '20px',
                overflow: 'hidden',
                background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.08)',
                animation: `skelPulse 1.4s ease-in-out ${i * 0.08}s infinite`,
                '@keyframes skelPulse': {
                  '0%, 100%': { opacity: 0.55 },
                  '50%': { opacity: 1 },
                },
              }}
            >
              <Box sx={{ width: '100%', aspectRatio: '3/4', background: 'rgba(255,255,255,0.06)' }} />
              <Box sx={{ p: 1.75 }}>
                <Box sx={{ height: 10, borderRadius: 1, background: 'rgba(255,255,255,0.08)', mb: 1 }} />
                <Box sx={{ height: 10, width: '70%', borderRadius: 1, background: 'rgba(255,255,255,0.08)', mb: 1.2 }} />
                <Box sx={{ height: 16, width: '40%', borderRadius: 1, background: 'rgba(255,255,255,0.1)' }} />
              </Box>
            </Box>
          ))}
        </Box>
      )}

      {/* Empty state */}
      {!loading && filteredProducts.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 8, px: 4 }}>
          <Typography sx={{ fontSize: '40px', mb: 1 }}>
            {activeFilter === 'donna' ? '👗' : activeFilter === 'uomo' ? '👔' : '🔍'}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '15px', fontWeight: 500 }}>
            Nessun capo trovato
          </Typography>
        </Box>
      )}

      {/* Product Grid */}
      {!loading && filteredProducts.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 2,
            px: 2,
            pb: 'calc(24px + env(safe-area-inset-bottom))',
          }}
        >
          {filteredProducts.map((item, idx) => (
            <Box
              key={item.id}
              onClick={() => handleSelectItem(item)}
              sx={{
                borderRadius: '20px',
                overflow: 'hidden',
                background: 'rgba(255,255,255,0.07)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255,255,255,0.1)',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 16px 40px rgba(0,0,0,0.3)',
                  background: 'rgba(255,255,255,0.12)',
                  border: '1px solid rgba(255,255,255,0.2)',
                },
                '&:active': {
                  transform: 'scale(0.97)',
                },
              }}
            >
              {/* Image */}
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '3/4',
                  overflow: 'hidden',
                  background: 'rgba(255,255,255,0.03)',
                  position: 'relative',
                }}
              >
                <Box
                  component="img"
                  src={getThumbUrl(item.imageUrl || item.immagine_url, 400)}
                  alt={item.name || item.nome}
                  loading={idx < 4 ? 'eager' : 'lazy'}
                  fetchpriority={idx < 4 ? 'high' : 'low'}
                  decoding="async"
                  sx={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    transition: 'transform 0.4s ease',
                    '&:hover': { transform: 'scale(1.05)' },
                  }}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
                {/* Gender badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 8,
                    left: 8,
                    fontSize: '10px',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    color: 'white',
                    background: item.genere === 'donna'
                      ? 'linear-gradient(135deg, #f093fb, #f5576c)'
                      : 'linear-gradient(135deg, #667eea, #4834d4)',
                    px: 1,
                    py: 0.3,
                    borderRadius: '8px',
                  }}
                >
                  {item.genere}
                </Box>
              </Box>

              {/* Info */}
              <Box sx={{ p: 1.75 }}>
                <Typography
                  sx={{
                    color: 'white',
                    fontSize: '13.5px',
                    fontWeight: 700,
                    lineHeight: 1.4,
                    mb: 0.5,
                    minHeight: '38px',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                  }}
                >
                  {item.name || item.nome}
                </Typography>

                {/* Color + brand */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                  {item.colore_principale && (
                    <Typography
                      sx={{
                        color: 'rgba(255,255,255,0.45)',
                        fontSize: '11px',
                        fontWeight: 500,
                      }}
                    >
                      {item.colore_principale}
                    </Typography>
                  )}
                </Box>

                {/* Price */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75 }}>
                  <Typography
                    sx={{
                      color: 'white',
                      fontSize: '18px',
                      fontWeight: 800,
                    }}
                  >
                    €{Number(item.price || item.prezzo).toFixed(2)}
                  </Typography>
                  {item.originalPrice && item.originalPrice > (item.price || item.prezzo) && (
                    <Typography
                      sx={{
                        color: 'rgba(255,255,255,0.35)',
                        fontSize: '12px',
                        textDecoration: 'line-through',
                      }}
                    >
                      €{Number(item.originalPrice).toFixed(2)}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export default CatalogBrowse;
